/* ─────────────────────────────────────────────────────────────
   AlertasModule.jsx — DMU 614
   Pantalla independiente de alertas (extraída del SupervisorDashboard).
   Misma lógica del tab original; solo cambia el punto de entrada.
   ───────────────────────────────────────────────────────────── */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../auth/AuthContext";
import ModoDemoBanner from "../components/ModoDemoBanner";
import "../styles/supervisor.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AlertasModule() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [verDemo, setVerDemo] = useState(false);
  const [data, setData] = useState(null);
  const [umbral, setUmbral] = useState(7);
  const [notif, setNotif] = useState({});      // subpunto_id → {ok, loading, error}
  const [filtroEspacio, setFiltroEspacio] = useState("");

  const cargar = async () => {
    try {
      const r = await axios.get(`${API}/alertas/subpuntos-desatendidos`, { params: { umbral_dias: umbral } });
      setData(r.data);
      setFiltroEspacio("");
    } catch (e) { toast.error(e.message); }
  };

  const itemsFiltrados = data
    ? (data.items || []).filter((it) =>
        !filtroEspacio || (it.espacio_nombre || "").toLowerCase().includes(filtroEspacio.toLowerCase())
      )
    : [];

  const hoyStr = () => new Date().toISOString().slice(0, 10);

  const descargarCSV = () => {
    if (!data) return;
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const headers = ["Espacio", "Sub-punto", "Última atención", "Días sin atención"];
    const rows = itemsFiltrados.map((it) => [it.espacio_nombre, it.nombre, it.ultima_atencion || "Nunca", it.dias_sin_atencion ?? ""]);
    const lines = [headers.map(esc).join(","), ...rows.map((r) => r.map(esc).join(","))];
    const blob = new Blob(["﻿" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `alertas_${umbral}d_${hoyStr()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [umbral]);

  const calcDias = (ultima) => {
    if (!ultima) return null;
    try {
      const d = new Date(ultima);
      const diff = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
      return diff > 0 ? diff : 0;
    } catch { return null; }
  };

  const notificar = async (it) => {
    const k = it.subpunto_id;
    setNotif((s) => ({ ...s, [k]: { loading: true } }));
    try {
      const body = {
        espacio_id: it.espacio_id,
        espacio_nombre: it.espacio_nombre,
        subpunto_id: it.subpunto_id,
        sub_punto: it.nombre,
        dias_sin_atencion: calcDias(it.ultima_atencion),
        ultima_atencion: it.ultima_atencion,
      };
      const r = await axios.post(`${API}/alertas/notificar-chofer`, body);
      setNotif((s) => ({ ...s, [k]: { ok: true, choferNombre: r.data.chofer_nombre, yaExistia: r.data.ya_existia } }));
      toast.success(
        r.data.ya_existia
          ? `Ya se había notificado hoy a ${r.data.chofer_nombre}`
          : `Notificado a ${r.data.chofer_nombre}`
      );
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      setNotif((s) => ({ ...s, [k]: { error: msg } }));
      toast.error(msg);
    }
  };

  return (
    <div className="sup-shell" data-testid="page-alertas">
      <ModoDemoBanner verDemo={verDemo} setVerDemo={setVerDemo} />
      <header className="sup-topbar">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/hub")} className="iconbtn-light" data-testid="btn-back-hub">
            <i className="fa-solid fa-arrow-left" />
          </button>
          <div>
            <div className="sup-title"><i className="fa-solid fa-bell mr-2"/>Alertas</div>
            <div className="sup-sub">
              {user?.nombre} · <span className="text-sky-300">{user?.rol}</span>
            </div>
          </div>
        </div>
        <button onClick={logout} className="iconbtn-light" data-testid="btn-logout">
          <i className="fa-solid fa-right-from-bracket" />
        </button>
      </header>

      <main className="sup-main">
        <div className="sup-pane" data-testid="alertas-panel">
          <h2 className="sup-h2">Alertas — sub-puntos desatendidos</h2>
          <div className="sup-filters">
            <label className="text-sm">Umbral (días):</label>
            <input type="number" value={umbral} onChange={(e) => setUmbral(parseInt(e.target.value || 7))} className="border rounded p-1 w-20" data-testid="input-umbral" />
            <button className="btn-primary" onClick={cargar} data-testid="btn-cargar-alertas">Recargar</button>
          </div>

          {data && (
            <>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="text-lg font-semibold">{data.total} sub-puntos &gt; {data.umbral_dias} días sin atención</span>
                <input
                  type="text"
                  placeholder="Filtrar por espacio…"
                  value={filtroEspacio}
                  onChange={(e) => setFiltroEspacio(e.target.value)}
                  className="border rounded p-1 flex-1 min-w-0"
                  data-testid="input-filtro-alertas"
                />
                <button className="btn-ghost-sm" onClick={descargarCSV} title="Descargar CSV" data-testid="btn-descargar-alertas">
                  ⬇ CSV
                </button>
              </div>
              <table className="sup-table" data-testid="tabla-alertas">
                <thead><tr><th>Espacio</th><th>Sub-punto</th><th>Última atención</th><th>Acción</th></tr></thead>
                <tbody>
                  {itemsFiltrados.map((it) => {
                    const st = notif[it.subpunto_id] || {};
                    return (
                      <tr key={it.subpunto_id} data-testid={`row-alerta-${it.subpunto_id}`}>
                        <td>{it.espacio_nombre}</td>
                        <td>{it.nombre}</td>
                        <td>{it.ultima_atencion || <span className="text-red-600">Nunca</span>}</td>
                        <td>
                          {st.ok ? (
                            <button className="btn-ghost-sm" disabled
                                    data-testid={`btn-notificado-${it.subpunto_id}`}
                                    style={{ background: "#DCFCE7", color: "#166534", borderColor: "#86EFAC" }}>
                              <i className="fa-solid fa-check"/> Notificado ✓
                            </button>
                          ) : (
                            <button className="btn-ghost-sm"
                                    disabled={st.loading}
                                    data-testid={`btn-notificar-${it.subpunto_id}`}
                                    onClick={() => notificar(it)}>
                              {st.loading ? "Enviando…" : "Notificar chofer"}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {itemsFiltrados.length === 0 && <tr><td colSpan="4" className="text-center text-slate-400 py-4">
                    {filtroEspacio ? "Sin resultados para ese filtro" : "Sin alertas — todo al día 🎉"}
                  </td></tr>}
                </tbody>
              </table>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
